/**
 * StateChip — THE canonical six-state chip under the K3 Black + Orange law.
 *
 * Design law: docs/plans/2026-07-24-k3-black-orange-design-law.md §2
 * Two chromatic hues in the whole system: orange (act/review) and red (stop).
 * Verified is deliberately HUELESS — calm ink + check glyph on elevated bg;
 * the absence of signal IS the signal. Every state carries icon + words —
 * never color-only. Fill strength is the primary axis:
 * solid → tint → outline → ghost = act-now → review → resting → dormant.
 *
 * Six states:
 *   blocker       red tint fill, octagon-X, semibold   (error / blocked / contested)
 *   needs-human   orange tint fill, user-check          (a human must look)
 *   warning       orange OUTLINE only, triangle         (icon + fill distinguish from needs-human)
 *   system-active orange tint, borderless, loader       (system running ≠ human task)
 *   verified      hueless gray on elevated, check       (the QUIETEST chip)
 *   dormant       ghost, hollow circle, 60% opacity     (not-run / queued — text should say
 *                                                        "NOT RUN", never an em-dash)
 *
 * Token notes: red/verified tokens (`--color-blocker*`, `--color-verified`) ship
 * in the K3 tokens.css; hex fallbacks below match the spec so the chip renders
 * the law correctly even before that file lands. Orange states ride the existing
 * theme-aware brand tokens (which K3 aliases onto `--accent`).
 */

import React from 'react';
import { cn } from '../../lib/utils';

export type ChipState =
  | 'blocker'
  | 'needs-human'
  | 'warning'
  | 'system-active'
  | 'verified'
  | 'dormant';

/**
 * Per-state chip container classes (fill / text / border / weight).
 * Exported so siblings (StatusBadge, MetricCard, provenance chips) share one law.
 */
export const STATE_STYLES: Record<ChipState, string> = {
  blocker:
    'bg-[var(--color-blocker-tint,rgba(229,72,77,0.12))] text-[var(--color-blocker,#E5484D)] border-[var(--color-blocker-tint-bd,rgba(229,72,77,0.25))] font-semibold',
  'needs-human':
    'bg-[var(--color-needs-human-tint)] text-[var(--color-needs-human-text)] border-[var(--color-needs-human-tint-bd)] font-medium',
  warning:
    'bg-transparent text-[var(--color-needs-human-text)] border-[var(--color-needs-human-tint-bd)] font-medium',
  'system-active':
    'bg-[var(--color-needs-human-tint)] text-[var(--color-needs-human-text)] border-transparent font-normal',
  verified:
    'bg-[var(--color-bg-elevated)] text-[var(--color-verified,var(--color-text-secondary))] border-[var(--color-hairline)] font-normal',
  dormant:
    'bg-transparent text-[var(--color-text-dim)] border-[var(--color-border-sub)] font-normal opacity-60',
};

/* Inline 13px stroke icons — currentColor, no dependency. Shapes are the
 * colorblind-safe redundant cue: octagon-X / user-check / triangle / loader /
 * check / hollow circle are distinct silhouettes even in grayscale. */

const iconProps = {
  width: 13,
  height: 13,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  className: 'shrink-0',
};

const OctagonXIcon = () => (
  <svg {...iconProps}>
    <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const UserCheckIcon = () => (
  <svg {...iconProps}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </svg>
);

const TriangleAlertIcon = () => (
  <svg {...iconProps}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const LoaderIcon = () => (
  <svg {...iconProps} className={cn(iconProps.className, 'motion-safe:animate-spin')}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const CheckIcon = () => (
  <svg {...iconProps}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const HollowCircleIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="9" />
  </svg>
);

export const STATE_ICONS: Record<ChipState, React.FC> = {
  blocker: OctagonXIcon,
  'needs-human': UserCheckIcon,
  warning: TriangleAlertIcon,
  'system-active': LoaderIcon,
  verified: CheckIcon,
  dormant: HollowCircleIcon,
};

interface StateChipProps {
  state: ChipState;
  text: string;
  /** DM Mono text — for timestamps / figures (e.g. "Attested · 2026-07-24 14:03"). */
  mono?: boolean;
  /** 'sm' is the canonical 26px chip; 'md' exists for StatusBadge size parity. */
  size?: 'sm' | 'md';
  className?: string;
}

export function StateChip({ state, text, mono = false, size = 'sm', className }: StateChipProps) {
  const Icon = STATE_ICONS[state];
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border',
        size === 'sm' ? 'h-[26px] gap-1.5 px-2.5 text-[12px]' : 'h-[30px] gap-2 px-3 text-sm',
        mono ? 'font-mono' : 'font-sans',
        STATE_STYLES[state],
        className
      )}
    >
      <Icon />
      <span>{text}</span>
    </span>
  );
}

export default StateChip;
